// Fast +1/−1 inventory counters (no dancer scan).
// Source of truth is iabf-config-{year}/room-inventory/rooms/{id}.reserved.

import { db } from '@/firebase'
import { IABF_REGISTRATION_YEAR, col } from '@/config/iabf-event'
import { getRegistrationRoomAvailability } from '@/utils/iabf-registration-api'

const TOTAL_TICKETS_ID = 'total_tickets'

const STAY_KEYS = [
  'Standard Dorm',
  'ADA Dorm',
  'Cottage',
  'Staying with another dancer',
  'Staying offsite'
]

export function normalizeStayForInventory (room) {
  const r = String(room || '').trim()
  if (r === 'Staying offsite (shuttle)' || r === 'Staying offsite (no shuttle)') {
    return 'Staying offsite'
  }
  if (r === 'Larger Dorm') {
    return 'ADA Dorm'
  }
  return r
}

function stayOccupiesInventory (room) {
  return STAY_KEYS.indexOf(normalizeStayForInventory(room)) !== -1
}

function roomInventoryDocId (room) {
  if (room === TOTAL_TICKETS_ID) {
    return TOTAL_TICKETS_ID
  }
  return normalizeStayForInventory(room).replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '') || 'room'
}

function inventoryRef (room, year) {
  return db.collection(col('config', year)).doc('room-inventory')
    .collection('rooms').doc(roomInventoryDocId(room))
}

async function setReserved (ref, extra, next) {
  await ref.set({
    ...extra,
    reserved: Math.max(0, next),
    updatedAt: Date.now()
  }, { merge: true })
}

async function bumpReserved (ref, extra, delta) {
  if (!delta) {
    return
  }
  const snap = await ref.get()
  const current = Number((snap.exists && snap.data() && snap.data().reserved) || 0) || 0
  await setReserved(ref, extra, current + delta)
}

/**
 * Move or add/remove a stay on the running counters.
 * totalDelta: +1 new enrollment, −1 cancel/delete, 0 when only the room type changes.
 */
export async function adjustIabfStayInventory ({
  year = IABF_REGISTRATION_YEAR,
  fromRoom,
  toRoom,
  totalDelta = 0
} = {}) {
  const y = Number(year) || IABF_REGISTRATION_YEAR
  if (y !== IABF_REGISTRATION_YEAR) {
    return
  }
  const from = stayOccupiesInventory(fromRoom) ? normalizeStayForInventory(fromRoom) : ''
  const to = stayOccupiesInventory(toRoom) ? normalizeStayForInventory(toRoom) : ''
  if (from && from === to && !totalDelta) {
    return
  }
  if (from && from === to) {
    await bumpReserved(inventoryRef(TOTAL_TICKETS_ID, y), { kind: 'total_tickets' }, totalDelta)
    return
  }
  if (from) {
    await bumpReserved(inventoryRef(from, y), { room: from }, -1)
  }
  if (to) {
    await bumpReserved(inventoryRef(to, y), { room: to }, 1)
  }
  if (totalDelta) {
    await bumpReserved(inventoryRef(TOTAL_TICKETS_ID, y), { kind: 'total_tickets' }, totalDelta)
  }
}

export function refreshRegistrationInventory () {
  return getRegistrationRoomAvailability({ reconcile: true }).catch((e) => {
    console.warn('refreshRegistrationInventory failed', e)
  })
}

export function recountRegistrationInventory () {
  return getRegistrationRoomAvailability({ recount: true })
}